/**
 * Provides common API logic and form handling for TCR brand, campaign, 
 * and toll-free verification data
 */

export default {
    data() {
        return {
            mixin_tcr_form_data: {},
            mixin_tcr_field_validity: {},
            mixin_tcr_loading: false,
            mixin_tcr_saving: false,
            mixin_tcr_submitting: false,
            mixin_biz_category_choices: [
                "Agriculture",
                "Media and Communication",
                "Construction, Materials, and Trade Services",
                "Education",
                "Energy and Utilities",
                "Entertainment",
                "Financial Services",
                "Gambling and Lottery",
                "Government Services and Agencies",
                "Healthcare and Life Sciences",
                "Hospitality and Travel",
                "HR, Staffing or Recruitment",
                "Insurance",
                "Legal",
                "Manufacturing",
                "Non-profit Organization",
                "Political",
                "Postal and Delivery",
                "Professional Services",
                "Real Estate",
                "Retail and Consumer Products",
                "Information Technology Services",
                "Transportation or Logistics",
            ],
            mixin_privacy_policy_addendum_options: [
                { text: 'Adopt and use the RedChirp text messaging privacy policy addendum', value: 'use-form-addendum' },
                { text: 'We have updated our website\'s privacy policy to satisfy carrier requirements', value: 'do-not-use-form-addendum' },
            ],
            mixin_terms_and_conditions_addendum_options: [
                { text: 'Adopt and use the RedChirp text messaging terms and conditions addendum', value: 'use-form-addendum' },
                { text: 'We have updated our website\'s terms and conditions to satisfy carrier requirements', value: 'do-not-use-form-addendum' },
            ],
        };
    },

    computed: {
        mixin_organization_id() {
            return this.$rc.$state.profile.employee_account.organization_id;
        },

        mixin_state_or_province() {
            return !this.mixin_tcr_form_data.country || this.mixin_tcr_form_data.country === 'us' ? 'State' : 'Province';
        },

        mixin_org_info_complete() {
            if (!this.mixinFieldsValid('organization_legal_name')) return false;
            if (this.mixin_tcr_form_data.has_dba && !this.mixinFieldsValid('dba')) return false;
            if (!this.mixinFieldsValid('tcr_legal_form')) return false;
            if (!this.mixinFieldsValid('tax_id')) return false;
            if (!this.mixinFieldsValid('tcr_business_category')) return false;
            return true;
        },

        mixin_contact_complete() {
            return this.mixinFieldsValid(
                'business_contact_first_name',
                'business_contact_last_name',
                'tcr_contact_email',
                'tcr_phone_number',
            );
        },

        mixin_address_complete() {
            return this.mixinFieldsValid(
                'business_address',
                'business_address_city',
                'business_address_state',
                'business_address_zip',
                'country'
            );
        },

        mixin_website_complete() {
            return this.mixinFieldsValid('website');
        },

        mixin_policies_complete() {
            return this.mixinFieldsValid(
                'url_privacy_policy',
                'privacy_policy_addendum',
                'url_terms_and_conditions',
                'terms_and_conditions_addendum'
            );
        },

        mixin_sections_complete() {
            let count = 0;
            if (this.mixin_org_info_complete) count++;
            if (this.mixin_contact_complete) count++;
            if (this.mixin_address_complete) count++;
            if (this.mixin_website_complete) count++;
            if (this.mixin_policies_complete) count++;
            return count;
        },

        mixin_is_form_valid() {
            return this.mixin_sections_complete === 5;
        },
    },

    methods: {
        /**
         * Check if all specified fields are valid
         * @param {...string} fields - Field names to check
         * @returns {boolean}
         */
        mixinFieldsValid(...fields) {
            return fields.every(f => this.mixin_tcr_field_validity[f] === true);
        },

        /**
         * Handle field validation event from rc-base-input or rc-select
         * @param {string} field - Field name
         * @param {boolean} is_valid - Whether the field is valid
         */
        mixinOnFieldValidated(field, is_valid) {
            this.$set(this.mixin_tcr_field_validity, field, is_valid);
        },

        /**
         * Load TCR organization setup data from the API
         * @param {Object} options - Options
         * @param {Function} [options.on_success] - Callback on success (receives data)
         * @param {Function} [options.on_error] - Callback on error
         * @returns {Promise<void>}
         */
        mixinLoadTcrFormData(options = {}) {
            const organization_id = this.mixin_organization_id;
            if (!organization_id) {
                options.on_error?.();
                return;
            }

            this.mixin_tcr_loading = true;

            this.$rc.rest({
                spinner: false,
                endpoint: `GET: /api/v1/welcome/tcr-organization-setup-data/${organization_id}`,
                on_success: (data) => {
                    this.mixin_tcr_form_data = { ...(data || {}) };
                    this.mixin_tcr_loading = false;
                    options.on_success?.(data);
                },
                on_error: () => {
                    this.mixin_tcr_loading = false;
                    options.on_error?.();
                }
            });
        },

        /**
         * Save TCR organization setup data to the API
         * @param {Object} options - Options
         * @param {Object} [options.form_data] - Override form data (uses mixin_tcr_form_data if not provided)
         * @param {Function} [options.on_success] - Callback on success (receives data)
         * @param {Function} [options.on_error] - Callback on error
         * @param {string} [options.success_message] - Snackbar message on success
         * @param {string} [options.error_message] - Snackbar message on error
         */
        mixinSaveTcrFormData(options = {}) {
            this._mixinPersistTcrFormData({
                ...options,
                loading_key: 'mixin_tcr_saving',
            });
        },

        /**
         * Submit TCR organization setup data (validates first)
         * @param {Object} options - Options
         * @param {boolean} [options.is_valid] - Override form validity check
         * @param {Object} [options.form_data] - Override form data (uses mixin_tcr_form_data if not provided)
         * @param {Function} [options.on_success] - Callback on success (receives data)
         * @param {Function} [options.on_error] - Callback on error
         * @param {string} [options.success_message] - Snackbar message on success
         * @param {string} [options.error_message] - Snackbar message on error
         * @param {string} [options.validation_error_message] - Snackbar message when form is invalid
         */
        mixinSubmitTcrFormData(options = {}) {
            const is_valid = options.is_valid !== undefined ? options.is_valid : this.mixin_is_form_valid;
            
            if (!is_valid) {
                this.$rc.snackbar(options.validation_error_message || 'Please complete all required fields before submitting.');
                return;
            }

            this._mixinPersistTcrFormData({
                ...options,
                loading_key: 'mixin_tcr_submitting',
            });
        },

        /**
         * Internal helper for saving/submitting TCR form data
         * @private
         */
        _mixinPersistTcrFormData(options = {}) {
            const organization_id = this.mixin_organization_id;
            if (!organization_id) {
                options.on_error?.();
                return;
            }

            const loading_key = options.loading_key || 'mixin_tcr_saving';
            this[loading_key] = true;

            const form_data = options.form_data || this.mixin_tcr_form_data;

            this.$rc.rest({
                spinner: false,
                endpoint: 'PUT: /api/v1/welcome/tcr-organization-setup-data',
                params: {
                    organization_id,
                    ...form_data,
                },

                on_success: (data) => {
                    this[loading_key] = false;
                    if (options.success_message) {
                        this.$rc.snackbar(options.success_message);
                    }
                    options.on_success?.(data);
                },

                on_error: () => {
                    this[loading_key] = false;
                    if (options.error_message) {
                        this.$rc.snackbar(options.error_message);
                    }
                    options.on_error?.();
                },
            });
        },

        /**
         * Handle URL field blur - normalizes URL format
         * @param {string} field - Field name in mixin_tcr_form_data
         * @param {Object} [form_data] - Override form data object (uses mixin_tcr_form_data if not provided)
         */
        mixinOnUrlBlur(field, form_data = null) {
            const data = form_data || this.mixin_tcr_form_data;
            const val = data[field];
            if (val) {
                data[field] = this.$rc.maskUrl(val);
            }
        },
    },
};
